import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop';
import BackToTopButton from './components/BackToTopButton';
import RequestSystem from './components/RequestSystem/RequestSystem';
import { RequestModalProvider } from './contexts/RequestModalContext';
import SiteAnalyticsTracker from './components/Analytics/SiteAnalyticsTracker';
import TrackingConsentLoader from './components/CookieBanner/TrackingConsentLoader';
import CookieBanner from './components/CookieBanner/CookieBanner';
import CartBootstrap from './components/Cart/CartBootstrap';
import CartDataSync from './components/Cart/CartDataSync';
import CartSnackbar from './components/Cart/CartSnackbar';
import FloatingCartSummary from './components/Cart/FloatingCartSummary';
import LoadingAnimation from './components/LoadingAnimation';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Route pages are code-split: each loads as its own chunk on demand instead of
// shipping in the initial 1.1 MB bundle. Always-mounted chrome stays static.
const HomePage = lazy(() => import('./pages/HomePage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const HowToBuyPage = lazy(() => import('./pages/HowToBuyPage'));
const ProductDetailsPage = lazy(() => import('./pages/ProductDetailsPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ReturnsPage = lazy(() => import('./pages/ReturnsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderStatusPage = lazy(() => import('./pages/OrderStatusPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const UserAgreementPage = lazy(() => import('./pages/UserAgreementPage'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ChineseHomePage = lazy(() => import('./pages/ChineseHomePage'));

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
      <RequestModalProvider>
        <ScrollToTop />
        <SiteAnalyticsTracker />
        <TrackingConsentLoader />
        <CartBootstrap />
        <CartDataSync />
        <Navbar />
        <Suspense fallback={<LoadingAnimation />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/catalog/:categoryId" element={<CatalogPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/company" element={<CompanyPage />} />
            <Route path="/how-to-buy" element={<HowToBuyPage />} />
            <Route path="/products/:productId" element={<ProductDetailsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-status" element={<OrderStatusPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/agreement" element={<UserAgreementPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/chinese/*" element={<ProtectedRoute><ChineseHomePage /></ProtectedRoute>} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
        <Footer />
        <BackToTopButton />
        <FloatingCartSummary />
        <CartSnackbar />
        <CookieBanner />
        <RequestSystem />
      </RequestModalProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
